import EmailIcon from '@mui/icons-material/Email';
import GoogleIcon from '@mui/icons-material/Google';
import LockIcon from '@mui/icons-material/Lock';
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  emailSignIn,
  googleSignIn,
} from '../../../../../corelogic/state/authentication/authentication.action';
import { store } from '../../../../../corelogic/state/store';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useUserProfile } from '../../hooks/useUserProfile';
// 🔒 Security Imports
import { ValidationResult } from '../../../../../corelogic/domain/utils/realtimeSecurityValidation.utils';
import { SecureTextField } from '../Shared/SecureTextField/SecureTextField';

// Styling
const styles = {
  container: {
    minHeight: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    pt: 1,
    pb: 1,
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.9)), url('/viewer_octagon.png')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    flex: 1,
    position: 'relative',
    overflow: 'auto',
  },
  loginBox: {
    width: '100%',
    maxWidth: '450px',
    height: 'auto',
    padding: '10px',
    borderRadius: '8px',
    backgroundColor: 'rgba(20, 20, 20, 0.9)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)',
    textAlign: 'center',
  },
  logo: {
    maxWidth: '150px',
    marginBottom: '20px',
    filter: 'drop-shadow(0 2px 5px rgba(0, 0, 0, 0.5))',
  },
  title: {
    fontFamily: 'Rubik Mono One',
    color: '#ffffff',
    fontSize: 'clamp(1.3rem, 2vw, 1.8rem)',
    marginBottom: '20px',
    textTransform: 'uppercase',
  },
  googleButton: {
    backgroundColor: '#8B0000',
    color: 'white',
    fontWeight: 'bold',
    padding: '10px 0',
    marginBottom: '15px',
    width: '100%',
    '&:hover': {
      backgroundColor: '#aa0000',
    },
  },
  divider: {
    color: 'rgba(255, 255, 255, 0.5)',
    margin: '15px 0',
    width: '100%',
    fontFamily: 'Rubik Mono One',
    fontSize: '0.8rem',
    '&::before, &::after': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
  },
  input: {
    marginBottom: '25px',
    width: '100%',
    '& .MuiInputBase-input': {
      color: 'white',
    },
    '& .MuiOutlinedInput-root': {
      color: 'white',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
      '&:hover fieldset': {
        borderColor: '#cc0000',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#cc0000',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
      '&.Mui-focused': {
        color: '#8B0000',
      },
    },
    '& .MuiInputAdornment-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  emailButton: {
    backgroundColor: '#8B0000',
    color: 'white',
    fontWeight: 'bold',
    padding: '10px 0',
    marginTop: '5px',
    marginBottom: '15px',
    width: '100%',
    '&:hover': {
      backgroundColor: '#aa0000',
    },
  },
  signupText: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '0.9rem',
    marginTop: '10px',
  },
  signupLink: {
    color: '#cc0000',
    textDecoration: 'none',
    fontWeight: 'bold',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
};

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Unified security validation state (matches established pattern)
  const [fieldValidations, setFieldValidations] = useState<
    Record<string, ValidationResult>
  >({});

  const navigate = useNavigate();
  const location = useLocation();

  const { isAuthenticated, isLoading: authLoading } = useAuthentication();
  const { isLoading: userLoading } = useUserProfile();
  const isLoading = authLoading || userLoading || isSubmitting;

  useEffect(() => {
    if (isAuthenticated) {
      // Redirect to the page they tried to visit or home
      const from = location.state?.from || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, location.state, navigate]);

  // Generic secure change handler (matches established pattern)
  const handleSecureChange = (
    field: string,
    value: string,
    validation: ValidationResult,
  ) => {
    // Update form values
    switch (field) {
      case 'email':
        setEmail(value);
        break;
    }

    // Update validation state
    setFieldValidations((prev) => ({ ...prev, [field]: validation }));
  };

  // Regular change handler for password (no XSS risk for password input)
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  // Check if there are any high-severity security issues (matches established pattern)
  const hasSecurityIssues = () => {
    return Object.values(fieldValidations).some((validation) =>
      validation.warnings.some((warning) => warning.severity === 'high'),
    );
  };

  // Direct sign in without security dialog
  const handleSignIn = () => {
    // Don't submit if there are security issues
    if (hasSecurityIssues()) {
      return; // Security messages are already shown below fields
    }

    setIsSubmitting(true);

    store.dispatch(
      emailSignIn({
        email: email.trim(),
        password,
        retry: 0,
      }),
    );
    setIsSubmitting(false);
  };

  // Handle Google Sign In (no XSS risk)
  const handleGoogleSignIn = () => {
    store.dispatch(googleSignIn({ retry: 0 }));
  };

  // Framer Motion Container
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <Box sx={styles.container}>
      <motion.div variants={container} initial="hidden" animate="show">
        <Paper elevation={0} sx={styles.loginBox}>
          <img
            src="/fantasy_mma_logo.png"
            alt="Fantasy MMA Logo"
            style={styles.logo}
          />
          <Typography variant="h4" sx={styles.title}>
            Sign In
          </Typography>

          <Button
            variant="contained"
            startIcon={<GoogleIcon />}
            sx={styles.googleButton}
            onClick={handleGoogleSignIn}
            disabled={isLoading}
          >
            {isLoading ? 'Please wait...' : 'Sign in with Google'}
          </Button>

          <Divider sx={styles.divider}>OR</Divider>

          {/* 🔒 Secure Email Input */}
          <SecureTextField
            label="Email"
            variant="outlined"
            type="email"
            value={email}
            onChange={(value, validation) =>
              handleSecureChange('email', value, validation)
            }
            fieldType="email"
            fieldName="Email"
            sx={styles.input}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Password field - no XSS protection needed for password input */}
          <TextField
            label="Password"
            variant="outlined"
            type="password"
            value={password}
            onChange={handlePasswordChange}
            sx={styles.input}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            sx={styles.emailButton}
            onClick={handleSignIn}
            disabled={isLoading || hasSecurityIssues()}
          >
            {isLoading ? 'Please wait...' : 'Sign in with Email'}
          </Button>
          <Typography sx={styles.signupText}>
            Don't have an account?{' '}
            <Link to="/signup" style={styles.signupLink}>
              Sign up here
            </Link>
          </Typography>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default Login;
